import { Button, MenuItem } from "@mui/material";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import { GKClientDataGrid, GKSelect, Grid2 } from "components";
import { CloseIcon } from "components/Icons";
import dayjs from "dayjs";
import DashboardLayout from "layouts/DashboardLayout";
import { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { MF_CATEGORIES } from "utils/constants";
import { extraColumnsField } from "utils/helpers";

// Returns and expense ratio already come as percent values
const formatValue = (value?: number | string | null) =>
  value === null || value === undefined || value === "" ? "-" : value;

const MF = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Coming back from the returns page keeps the last category selected
  const [selectedCategory, setSelectedCategory] = useState(
    searchParams.get("category") || ""
  );
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["mf-category-table", selectedCategory],
    queryFn: () =>
      // encodeURIComponent sends spaces as %20 (axios params would use +)
      axiosInstance.get(
        `/mf-screener/category-table?category=${encodeURIComponent(
          selectedCategory
        )}`
      ),
    enabled: !!selectedCategory,
    // Reuse the cached table when coming back from the returns page
    staleTime: Infinity,
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
  });

  const response = data?.data;
  const rows: any[] = response?.funds || [];

  const benchmarkLabel = response?.benchmark?.name || response?.meta?.benchmark;

  // Benchmark and Category Median stay pinned below the scheme rows
  const pinnedBottomRows = response
    ? [
        {
          schemeCode: "benchmark",
          name: benchmarkLabel ? `Benchmark (${benchmarkLabel})` : "Benchmark",
          returns: response?.benchmark?.returns,
        },
        {
          schemeCode: "category-median",
          name: "Category Median",
          returns: response?.categoryMedian?.returns,
        },
      ]
    : [];

  const returnColumn = (label: string, key: string): GridColDef => ({
    ...extraColumnsField(label, key, 110, 0),
    valueGetter: (row) => formatValue(row?.row?.returns?.[key]),
  });

  const columns: GridColDef[] = [
    {
      ...extraColumnsField("Scheme Name", "name", 300, 2),
    },
    {
      ...extraColumnsField("Category", "category", 140, 1),
      valueGetter: (row) => formatValue(row?.row?.category),
    },
    {
      ...extraColumnsField("Launch Date", "launchDate", 120, 0),
      valueGetter: (row) =>
        row?.row?.launchDate
          ? dayjs(row.row.launchDate).format("DD-MM-YYYY")
          : "-",
    },
    {
      ...extraColumnsField("AUM (Crore)", "aumCrore", 120, 0),
      valueGetter: (row) => formatValue(row?.row?.aumCrore),
    },
    {
      ...extraColumnsField("Expense Ratio (%)", "expenseRatio", 140, 0),
      valueGetter: (row) => formatValue(row?.row?.expenseRatio),
    },
    {
      ...extraColumnsField("Exit Load (Days)", "exitLoadDays", 130, 0),
      valueGetter: (row) => formatValue(row?.row?.exitLoadDays),
    },
    returnColumn("1-Year Ret (%)", "1y"),
    returnColumn("3-Yrs Ret (%)", "3y"),
    returnColumn("5-Yrs Ret (%)", "5y"),
    returnColumn("10-Yrs Ret (%)", "10y"),
    returnColumn("Since Launch Ret (%)", "sinceInception"),
  ];

  return (
    <DashboardLayout title="MF">
      <Grid2 container spacing={1}>
        <Grid2 size={{ xs: 12, md: 3 }}>
          <GKSelect
            size="small"
            inputLabel="Category"
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value as string);
              setSelectedRows([]);
            }}
            endAdornment={
              selectedCategory && (
                <span
                  role="button"
                  tabIndex={0}
                  style={{
                    display: "flex",
                    cursor: "pointer",
                    alignItems: "center",
                    paddingRight: 8,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedCategory("");
                    setSelectedRows([]);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.stopPropagation();
                      setSelectedCategory("");
                      setSelectedRows([]);
                    }
                  }}
                >
                  <CloseIcon />
                </span>
              )
            }
          >
            {MF_CATEGORIES.map((category) => (
              <MenuItem key={category} value={category}>
                {category}
              </MenuItem>
            ))}
          </GKSelect>
        </Grid2>
        {selectedRows.length > 0 && (
          <Grid2
            size={{ xs: 12, md: 9 }}
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
          >
            <Button
              variant="contained"
              // Pass the already fetched data so the next page needs no API call
              onClick={() =>
                navigate("/app/mf/returns", {
                  state: {
                    category: selectedCategory,
                    response: {
                      ...response,
                      funds: rows.filter((fund) =>
                        selectedRows.includes(fund.schemeCode)
                      ),
                    },
                  },
                })
              }
            >
              Submit ({selectedRows.length})
            </Button>
          </Grid2>
        )}
        {selectedCategory && (
          <Grid2 size={12}>
            <GKClientDataGrid
              columns={columns}
              rows={rows}
              getRowId={(row) => row.schemeCode}
              loading={isLoading || isFetching}
              showFiltersButton={false}
              checkboxSelection
              disableRowSelectionOnClick
              isRowSelectable={(params) =>
                !["category-median", "benchmark"].includes(String(params.id))
              }
              rowSelectionModel={selectedRows}
              onRowSelectionModelChange={setSelectedRows}
              pinnedRows={{ bottom: pinnedBottomRows }}
            />
          </Grid2>
        )}
      </Grid2>
    </DashboardLayout>
  );
};

export default MF;
